import React, {FormEvent, useEffect, useState} from "react";
import {connect, ConnectedProps} from "react-redux";
import CodUserAPI from "../../api/CodUserAPI";

import {Button, Card, Col, Form, Modal, Row, Image} from "react-bootstrap";
import * as Input from "../../inputs";
import * as Inputs from '../../inputs';
import * as Validators from '../../util/validators';
import Forms from "../../util/Forms";
import {Link} from "react-router-dom";
import AccountActions from "../../store/actions/AccountActions";
import {GeneralActions} from "../../store/actions/GeneralActions";
import styles from './CreateAccount.module.scss';
import MultiColumnInputElementForm from "../../components/MultiColumnInputElementForm/MultiColumnInputElementForm";
import * as filters from '../../util/filters';
import IconWithTextTable from "../IconWithTextTable/IconWithTextTable";
import { CityStateDTO, ContactMethodType, IconWithTextRecord, IndustryDTO, NewUserAndAccountDTO, PreferredContactMethodDTO, SimpleSelectOption, UserDTO } from "../../types";
import VerificationCodeModal from "../../components/VerificationCodeModal/VerificationCodeModal";
import CognitoAPI from "../../api/CognitoAPI";
import PasswordValidation from "../../util/PasswordValidation";
import UserAccessPage from "../../hoc/UserAccessPage/UserAccessPage";
import BillingAPI from "../../api/BillingAPI";

const map_state_to_props = null;

const map_dispatch_to_props = (dispatch: any) => ({
    actions: {
        show_spinning_wheel: () => dispatch(GeneralActions.show_spinning_wheel()),
        hide_spinning_wheel: () => dispatch(GeneralActions.hide_spinning_wheel()),
        show_error: (text: string) => dispatch(GeneralActions.show_error(text)),
        clear_error: () => dispatch(GeneralActions.clear_error()),
        go_home: () => dispatch(AccountActions.go_home()),
        set_error_from_response: (e: any) => dispatch(GeneralActions.set_error_from_response(e)),
    }
});

const connector = connect(map_state_to_props, map_dispatch_to_props);

interface AccountForm extends Record<string, Inputs.InputField> {
    first_name: Inputs.TextField;
    last_name: Inputs.TextField;
    email: Inputs.EmailField;
    phone: Inputs.PhoneNumberField;
    company_name: Inputs.TextField;
    address: Inputs.TextField;
    password: Inputs.PasswordField;
    confirm_password: Inputs.PasswordField;
    city: Inputs.SingleSelectField<CityStateDTO>;
    zip_code: Inputs.TextField;
    industry: Inputs.SingleSelectField<IndustryDTO>;
    preferred_contact_method: Inputs.SingleSelectField<PreferredContactMethodDTO>;
}

interface VerificationCodeForm extends Record<string, Inputs.InputField> {
    verification_code: Inputs.TextField;
    email: Inputs.ReadOnlyText;
}

const CreateAccount = ({actions}: ConnectedProps<typeof connector>) => {
    const [account_form, set_account_form] = useState<AccountForm>({
        first_name: {
            type: Inputs.FieldDataType.Text,
            label: 'First Name:',
            touched: false,
            valid: false,
            dom_id: 'create-account-first-name',
            name: 'firstname',
            value: '',
            hide_valid: true,
            placeholder: 'First Name',
            validator: Validators.required_text
        },
        last_name: {
            type: Inputs.FieldDataType.Text,
            label: 'Last Name:',
            touched: false,
            valid: false,
            dom_id: 'create-account-last-name',
            name: 'lastname',
            value: '',
            hide_valid: true,
            placeholder: 'Last Name',
            validator: Validators.required_text
        },
        email: {
            type: Inputs.FieldDataType.Email,
            label: 'Email:',
            touched: false,
            valid: false,
            dom_id: 'create-account-email',
            name: 'email',
            value: '',
            hide_valid: true,
            placeholder: 'Email',
            validator: Validators.required_text
        },
        phone: {
            type: Inputs.FieldDataType.PhoneNumber,
            touched: false,
            valid: false,
            hide_valid: true,
            dom_id: 'create-account-phone',
            name: 'phone',
            label: 'Phone:',
            value: '',
            filter: filters.max_length(10),
            validator: (input, _) => input.length === 10
        },
        company_name: {
            type: Inputs.FieldDataType.Text,
            label: 'Company Name:',
            touched: false,
            valid: false,
            dom_id: 'create-account-company-name',
            name: 'company-name',
            value: '',
            hide_valid: true,
            placeholder: 'Company Name',
            validator: Validators.required_text
        },
        address: {
            type: Inputs.FieldDataType.Text,
            label: 'Company Address:',
            touched: false,
            valid: false,
            dom_id: 'create-account-address1',
            name: 'address',
            value: '',
            hide_valid: true,
            placeholder: 'Company Address',
            validator: Validators.required_text
        },
        password: {
            type: Inputs.FieldDataType.Password,
            label: 'Password:',
            touched: false,
            valid: false,
            dom_id: 'create-account-pass',
            name: 'password',
            value: '',
            hide_valid: true,
            placeholder: 'Password',
            validator: Validators.combine_and([Validators.required_text, 
                Validators.does_not_contain_spaces,
                Validators.min_length(8),
                Validators.max_length(20),
                Validators.has_number,
                Validators.has_upper_case_letter,
                Validators.has_lower_case_letter,
                Validators.has_special_character,
                Validators.has_no_more_than_three_repetitive_characters,
                Validators.has_no_more_than_three_sequential_characters
            ])
        }, 
        confirm_password: {
            type: Inputs.FieldDataType.Password,
            touched: false,
            valid: false,
            dom_id: 'create-account-confirm-password',
            value: '',
            hide_valid: true,
            label: 'Confirm Password:',
            validator: (input, current_form) => (
                input === (current_form as AccountForm).password.value
            )
        },
        city: {
            type: Inputs.FieldDataType.SingleSelect,
            label: 'City and State:',
            touched: false,
            valid: true,
            dom_id: 'create-account-city',
            name: 'city',
            hide_valid: true,
            options: [{city: '', postal_code: '',  state: ''}],
            value: {city: '',  postal_code: '',  state: ''},
            option_to_display_string: city => city.postal_code !== '' ? `${city.city}, ${city.state}` : 'Enter a zipcode to select a city',
            option_to_value_string: city => city ? city.city : '',
            value_from_option_string: (value, options) => options.filter(option => option.city === value)[0]
        },
        zip_code: {
            type: Inputs.FieldDataType.Text,
            label: 'Zip Code:',
            touched: false,
            valid: false,
            dom_id: 'create-account-zip',
            name: 'zip',
            value: '',
            hide_valid: true,
            placeholder: 'Zip Code',
            filter: filters.combine_filters([filters.only_integer, filters.max_length(5)]),
            validator: (input, _) => input.length === 5
        },
        industry: {
            type: Inputs.FieldDataType.SingleSelect,
            label: 'Industry:',
            touched: false,
            valid: true,
            dom_id: 'create-account-industry',
            name: 'industry',
            hide_valid: true,
            options: [],
            value: {id: '-1', name: 'Select', order: -1},
            option_to_display_string: option => option.name,
            option_to_value_string: option => `${option.id}`,
            value_from_option_string: (value, options) => options.filter(option => option.id === value)[0],
            validator: (input, _) => input.id !== '-1'
        },
        preferred_contact_method: {
            type: Input.FieldDataType.SingleSelect,
            label: 'Preferred Contact Method:',
            touched: false,
            valid: false,
            dom_id: 'create-account-preferred-contact-method',
            name: 'preferred-contact-method',
            value: {id: '-1', name: '', order: -1},
            hide_valid: true,
            options: [],
            option_to_display_string: option => option.name,
            option_to_value_string: option => `${option.id}`,
            value_from_option_string: (value, options) => options.filter(option => option.id === value)[0]
        },
    });
    const [account_form_valid, set_account_form_valid] = useState<boolean>(false);
    const [zip_code_valid, set_zip_code_valid] = useState<boolean>(false);
    const [validation_list, set_validation_list] = useState<IconWithTextRecord[]>([]);
    const [show_validation_modal, set_show_validation_modal] = useState<boolean>(false);
    const [verification_code_form, set_verification_code_form] = useState<VerificationCodeForm>({
        email: {
            type: Inputs.FieldDataType.ReadOnlyText,
            label: 'Email Address:',
            touched: false,
            valid: false,
            dom_id: 'verification-email',
            name: 'email',
            value: '',
            hide_valid: true,
        },
        verification_code: {
            type: Inputs.FieldDataType.Text,
            label: 'Verification Code',
            touched: false,
            valid: false,
            dom_id: 'verification-code-name',
            name: 'verification_code',
            value: '',
            hide_valid: true,
            placeholder: 'Verification Code',
            floating_label: true,
            validator: Validators.required_text
        }
    });
    const [verification_code_form_valid, set_verification_code_form_valid] = useState<boolean>(false);
    const [account_guid, set_account_guid] = useState<string>('');

    const load_data = async () => {
        const [
            preferred_contact_methods,
            industries
        ] = await Promise.all([
            CodUserAPI.get_preferred_contact_methods(),
            CodUserAPI.get_industries()
        ]);

        set_account_form({
            ...account_form,
            preferred_contact_method: {
                ...account_form.preferred_contact_method,
                touched: true,
                valid: true,
                options: [...preferred_contact_methods].sort((a, b) => a.order - b.order),
                value: preferred_contact_methods[0]
            },
            industry: {
                ...account_form.industry,
                touched: true,
                valid: true,
                options: [...industries].sort((a, b) => a.order - b.order),
                value: industries[0]
            }
        })

        const list = PasswordValidation.get_initial_password_validation_list();
        set_validation_list(list);
    }

    useEffect(() => {
        load_data();
    }, [])

    useEffect(() => {
        search_city_state();
    }, [zip_code_valid])

    const account_form_changed = (key: string, value: any) => {
        const {form, form_valid} = Forms.update_form({key, value, form: account_form});
        const list = PasswordValidation.get_password_validation_list(form.password.value);
        set_validation_list(list);

        const updated_form = form as AccountForm;
        if (key === 'zip_code') {
            updated_form.zip_code = {
                ...updated_form.zip_code,
                error: undefined
            }
            set_zip_code_valid(updated_form.zip_code.value.length === 5);
        }
        set_account_form(updated_form);
        set_account_form_valid(form_valid);
    };

    const load_locations = async (zip_code: string) => {
        return await CodUserAPI.getCitiesStates(zip_code.trim());
    };

    const on_base_field_blur = async (key: string) => {
        const zip_code = account_form.zip_code.value;
        const current_postal_code = account_form.city.value ? account_form.city.value.postal_code : '';
        if (key === 'zip_code' && zip_code.length > 0) { 
            actions.show_spinning_wheel()
            const locations = await load_locations(zip_code);
            const selected_location = locations.find(city => city.postal_code === current_postal_code);

            const updated_form: AccountForm = {
                ...account_form,
                zip_code: {
                    ...account_form.zip_code,
                    valid: locations.length > 0,
                    error: locations.length === 0 ? "Please check your ZIP Code and make sure it is correct." : undefined
                },
                city: {
                    ...account_form.city,
                    options: locations,
                    value: selected_location || locations[0]
                } as Inputs.SingleSelectField<CityStateDTO>,

            };
            set_account_form(updated_form);
            actions.hide_spinning_wheel();
        }
    };

    const search_city_state = async () => {
        const zip_code = account_form.zip_code.value;
        const current_postal_code = account_form.city.value ? account_form.city.value.postal_code : '';
        if (zip_code.length === 5) { 
            actions.show_spinning_wheel()
            const locations = await load_locations(zip_code);
            const selected_location = locations.find(city => city.postal_code === current_postal_code);

            const updated_form: AccountForm = {
                ...account_form,
                zip_code: {
                    ...account_form.zip_code,
                    valid: locations.length > 0,
                    error: locations.length === 0 ? "Please check your ZIP Code and make sure it is correct." : undefined
                },
                city: {
                    ...account_form.city,
                    options: locations,
                    value: selected_location || locations[0]
                } as Inputs.SingleSelectField<CityStateDTO>,

            };
            set_account_form(updated_form);
            actions.hide_spinning_wheel();
        } else {
            const updated_form: AccountForm = {
                ...account_form,
                zip_code: {
                    ...account_form.zip_code,
                    valid: false,
                },
                city: {
                    ...account_form.city,
                    options: [],
                    value: {city: '',  postal_code: '',  state: ''}
                } as Inputs.SingleSelectField<CityStateDTO>,
            };
            set_account_form(updated_form);
            actions.hide_spinning_wheel();
        }
    };

    const account_form_submit = async (event: FormEvent) => {
        event.preventDefault();
        const {form, form_valid} = Forms.filter_and_validate_form(account_form);
        const final_form = form as AccountForm;

        if (!form_valid) {
            set_account_form(final_form);
            set_account_form_valid(form_valid);
            return;
        }

        const new_account_model: NewUserAndAccountDTO = {
            user: {
                first_name: final_form.first_name.value.trim(),
                last_name: final_form.last_name.value.trim(),
                preferred_contact_method: final_form.preferred_contact_method.value.id as ContactMethodType,
                phone_number: final_form.phone.value.trim(),
                email_address: final_form.email.value.trim()
            },
            account: {
                account_name: final_form.company_name.value.trim(),
                address: final_form.address.value.trim(),
                city: final_form.city.value.city.trim(),
                state: final_form.city.value.state.trim(),
                postal_code: final_form.zip_code.value.trim(),
                industry: final_form.industry.value.name.trim(),
                are_records_shared: false
            }
        };

        actions.show_spinning_wheel();
        try {
            set_verification_code_form({
                ...verification_code_form,
                email: {
                    ...verification_code_form.email,
                    touched: true,
                    valid: true,
                    value: new_account_model.user.email_address
                }
            });
            const payload = await CodUserAPI.createUser(new_account_model, final_form.confirm_password.value);
            set_account_guid(payload.account_guid ?? '');
            set_show_validation_modal(true);

          } catch (error: any) {
            window.scrollTo({top: 0})
            actions.set_error_from_response(error);
            actions.hide_spinning_wheel();
          }
          actions.hide_spinning_wheel();
    };

    const on_verification_code_submit = async (event: FormEvent) => {
        event.preventDefault();
        const {form, form_valid} = Forms.filter_and_validate_form(verification_code_form);
        const final_form = form as VerificationCodeForm;

        if (!form_valid) {
            set_verification_code_form(final_form);
            return;
        }
        try {
            actions.show_spinning_wheel();
            await CognitoAPI.submitVerificationCode(final_form.email.value, final_form.verification_code.value.trim());
            const user = await CognitoAPI.signIn(final_form.email.value, account_form.confirm_password.value) as UserDTO;
            await BillingAPI.createBillingAccount(account_guid, user.user_guid);
            await CognitoAPI.signOut();
            actions.hide_spinning_wheel();
            set_show_validation_modal(false);
            actions.go_home();     
        } catch (error: any) {
            actions.show_error(error.message);
            actions.hide_spinning_wheel();
        }
        actions.hide_spinning_wheel();
        
    };

    const on_verification_code_change  = (key: string, value: any) => {
        const {form, form_valid} = Forms.update_form({key, value, form: verification_code_form});
        set_verification_code_form(form as VerificationCodeForm);
        set_verification_code_form_valid(form_valid);
    };


    const on_verification_code_resend = async () => {
        try{
            await CognitoAPI.resendConfirmationCode(verification_code_form.email.value.trim());
        } catch (error: any) {
            actions.show_error(error.message);
        }    
    };

    const render_top_form = () => (
        <MultiColumnInputElementForm
            fields={account_form}
            changed={account_form_changed}
            // blurred={on_base_field_blur}
            group_className={'mb-3'}
            included_keys={['first_name', 'last_name', 'email', 'phone', 'preferred_contact_method', 'company_name', 'address', 'zip_code', 'city', 'industry']}
            columns={2}/>
    );

    const render_password_form = () => (
        <MultiColumnInputElementForm
            fields={account_form}
            changed={account_form_changed}
            group_className={'mb-3'}
            included_keys={['password', 'confirm_password']}
            columns={2} />
    );

    const render_bottom_rows = () => (
        <Row>
            <IconWithTextTable  records={validation_list} /> 
            <Row>
                <Col className={'d-flex justify-content-center mb-3'}>
                    <Button
                        type={'submit'}
                        variant={'teal'}
                        className={'rounded-pill px-4'}
                        disabled={!account_form_valid}
                    >Create Account</Button>
                </Col>
            </Row>
            <Row>
                <Col className={'text-center mb-2'}>
                        Already have an account? <Link to={'/login'}>Sign In</Link>
                </Col>
            </Row>
            <Row className={'mb-1 mt-2'}>
                <Col className={'text-center'}>
                    <sup>
                        Do not use "Remember me" on public or shared computers.
                    </sup>
                </Col>
            </Row>
        </Row>  
    );

    return (
        <UserAccessPage>
            <Col xs={12} className={'mb-3 mb-md-0'}>
                <Row className={'mb-3 text-center'}>
                    <h3>
                        <strong>Create My Account</strong>
                    </h3>
                    <div className={`${styles.Note}`}>
                        All fields below are required
                    </div>
                </Row>
                <Col className={'mb-3 mb-md-0'}>
                    <Form onSubmit={account_form_submit}>
                        {render_top_form()}
                        {render_password_form()}
                        {render_bottom_rows()}    
                                
                    </Form>
                </Col>
            </Col>
            <VerificationCodeModal
                show={show_validation_modal}
                on_hide={() => set_show_validation_modal(false)}
                size={'lg'}
                form={verification_code_form}
                formValid={verification_code_form_valid}
                onSubmit={on_verification_code_submit}
                onFormChange={on_verification_code_change}
                onVerificationCodeResend={on_verification_code_resend}
            />   
        </UserAccessPage>  
    );
}

export default connector(CreateAccount);